export const CLIENT_ONBOARDING_IMPORT_TYPES = [
  "Customer Master",
  "Accounts",
  "Invoices",
  "Open AR",
  "Statements",
  "Payments",
  "Promises",
  "Supporting Documents",
] as const;

export const CLIENT_ONBOARDING_REQUIRED_FIELDS = [
  "Customer Number",
  "Customer Name",
  "Account Number",
  "Invoice Number",
  "Balance",
  "Due Date",
] as const;

export const CLIENT_ONBOARDING_MAPPING_TARGETS = [
  "Ignore",
  "Customer Number",
  "Customer Name",
  "Customer Status",
  "Account Number",
  "Account Status",
  "Invoice Number",
  "Invoice Date",
  "Due Date",
  "Balance",
  "Aging Bucket",
  "Terms",
  "Salesperson",
  "Business Unit",
  "Collector",
  "Contact Name",
  "Contact Email",
  "Recipient Email",
  "Delivery Method",
  "Statement Number",
  "Statement Status",
  "Payment Configuration",
  "Payment Reference",
] as const;

export type ClientOnboardingImportType = typeof CLIENT_ONBOARDING_IMPORT_TYPES[number];
export type ClientOnboardingPortfolioRow = Record<string, string>;
export type ClientOnboardingValidationSummary = {
  validRecords: number;
  warnings: string[];
  errors: string[];
  duplicates: number;
  missingRequiredFields: number;
  invalidBalances: number;
  invalidDates: number;
};

export type ClientOnboardingValidationContext = {
  sourceName?: string;
  sheetName?: string;
  headerRow?: number;
};

const ALLOWED_ACCOUNT_STATUSES = new Set(["active", "open", "hold", "disputed", "closed", "inactive"]);
const ALLOWED_CUSTOMER_STATUSES = new Set(["active", "hold", "inactive"]);
const ALLOWED_AGING_BUCKETS = new Set(["current", "1-30", "31-60", "61-90", "91-120", "121+"]);

export function parseClientOnboardingDelimitedText(input: string) {
  const lines = input.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (!lines.length) return { headers: [] as string[], rows: [] as ClientOnboardingPortfolioRow[] };
  const delimiter = lines[0].includes("\t") ? "\t" : ",";
  const headers = normalizeDelimitedHeaders(parseDelimitedLine(lines[0], delimiter));
  const rows = lines.slice(1).map((line) => {
    const values = parseDelimitedLine(line, delimiter);
    return Object.fromEntries(headers.map((header, index) => [header, values[index]?.trim() || ""]));
  });
  return { headers, rows };
}

export function buildDefaultClientOnboardingMappings(headers: string[]) {
  return Object.fromEntries(headers.map((header) => [header, inferClientOnboardingTarget(header)]));
}

export function inferClientOnboardingTarget(header: string) {
  const normalized = header.toLowerCase();
  if (normalized.includes("customer") && normalized.includes("number")) return "Customer Number";
  if (normalized.includes("customer") && normalized.includes("status")) return "Customer Status";
  if (normalized.includes("customer") || normalized.includes("name")) return "Customer Name";
  if (normalized.includes("account") && normalized.includes("status")) return "Account Status";
  if (normalized.includes("account")) return "Account Number";
  if (normalized.includes("invoice") && normalized.includes("date")) return "Invoice Date";
  if (normalized.includes("invoice")) return "Invoice Number";
  if (normalized.includes("due")) return "Due Date";
  if (normalized.includes("balance") || normalized.includes("amount")) return "Balance";
  if (normalized.includes("aging")) return "Aging Bucket";
  if (normalized.includes("term")) return "Terms";
  if (normalized.includes("sales")) return "Salesperson";
  if (normalized.includes("business") || normalized.includes("unit")) return "Business Unit";
  if (normalized.includes("collector")) return "Collector";
  if (normalized.includes("recipient") && normalized.includes("email")) return "Recipient Email";
  if (normalized.includes("contact") && normalized.includes("email")) return "Contact Email";
  if (normalized.includes("contact")) return "Contact Name";
  if (normalized.includes("delivery") && normalized.includes("method")) return "Delivery Method";
  if (normalized.includes("statement") && normalized.includes("status")) return "Statement Status";
  if (normalized.includes("statement")) return "Statement Number";
  if (normalized.includes("payment") && normalized.includes("config")) return "Payment Configuration";
  if (normalized.includes("payment") || normalized.includes("reference")) return "Payment Reference";
  return "Ignore";
}

export function validateClientOnboardingRows(
  rows: ClientOnboardingPortfolioRow[],
  mappings: Record<string, string>,
  context: ClientOnboardingValidationContext = {},
): ClientOnboardingValidationSummary {
  const mappedEntries = Object.entries(mappings);
  const sourceFor = (target: string) => mappedEntries.find(([, mapped]) => mapped === target)?.[0] || "";
  const findValue = (row: ClientOnboardingPortfolioRow, target: string) => {
    const source = sourceFor(target);
    return source ? row[source] || "" : "";
  };
  const location = (rowIndex: number, target?: string) => {
    const source = target ? sourceFor(target) : "";
    const rowNumber = rowIndex + (context.headerRow || 1) + 1;
    const sourcePrefix = [context.sourceName, context.sheetName].filter(Boolean).join(" / ");
    return `${sourcePrefix ? `${sourcePrefix} - ` : ""}row ${rowNumber}${source ? `, ${source}` : ""}`;
  };

  const errors: string[] = [];
  const warnings: string[] = [];
  let missingRequiredFields = 0;
  let invalidBalances = 0;
  let invalidDates = 0;
  const invalidRows = new Set<number>();
  const seen = new Set<string>();
  let duplicates = 0;

  rows.forEach((row, index) => {
    const missing = CLIENT_ONBOARDING_REQUIRED_FIELDS.filter((field) => !findValue(row, field));
    if (missing.length) {
      missingRequiredFields += missing.length;
      invalidRows.add(index);
      for (const field of missing) errors.push(`${location(index, field)}: blank required value for ${field}.`);
    }

    const balance = findValue(row, "Balance").replace(/[$,\s]/g, "");
    if (balance && !Number.isFinite(Number(balance))) {
      invalidBalances += 1;
      invalidRows.add(index);
      errors.push(`${location(index, "Balance")}: balance must be numeric.`);
    }

    for (const target of ["Invoice Date", "Due Date"]) {
      const value = findValue(row, target);
      if (value && !isValidImportDate(value)) {
        invalidDates += 1;
        invalidRows.add(index);
        errors.push(`${location(index, target)}: invalid date value.`);
      }
    }

    const accountStatus = findValue(row, "Account Status").trim().toLowerCase();
    if (accountStatus && !ALLOWED_ACCOUNT_STATUSES.has(accountStatus)) {
      invalidRows.add(index);
      errors.push(`${location(index, "Account Status")}: unknown account status "${findValue(row, "Account Status")}".`);
    }
    const customerStatus = findValue(row, "Customer Status").trim().toLowerCase();
    if (customerStatus && !ALLOWED_CUSTOMER_STATUSES.has(customerStatus)) {
      invalidRows.add(index);
      errors.push(`${location(index, "Customer Status")}: unknown customer status "${findValue(row, "Customer Status")}".`);
    }

    const agingBucket = findValue(row, "Aging Bucket").trim().toLowerCase();
    if (agingBucket && !ALLOWED_AGING_BUCKETS.has(agingBucket)) {
      warnings.push(`${location(index, "Aging Bucket")}: aging bucket should be reviewed.`);
    }

    for (const target of ["Contact Email", "Recipient Email"]) {
      const email = findValue(row, target).trim();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        warnings.push(`${location(index, target)}: email format should be reviewed.`);
      }
    }

    const duplicateKey = [
      findValue(row, "Customer Number"),
      findValue(row, "Account Number"),
      findValue(row, "Invoice Number"),
    ].join("|");
    if (duplicateKey !== "||") {
      if (seen.has(duplicateKey)) {
        duplicates += 1;
        warnings.push(`${location(index, "Invoice Number")}: duplicate customer/account/invoice key.`);
      }
      seen.add(duplicateKey);
    }
  });

  if (!rows.length) warnings.push("Upload an XLSX, CSV, or TXT portfolio to begin validation.");
  return {
    validRecords: Math.max(0, rows.length - invalidRows.size - duplicates),
    warnings,
    errors,
    duplicates,
    missingRequiredFields,
    invalidBalances,
    invalidDates,
  };
}

function parseDelimitedLine(line: string, delimiter: string) {
  const result: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === "\"" && quoted && next === "\"") {
      current += "\"";
      index += 1;
      continue;
    }
    if (char === "\"") {
      quoted = !quoted;
      continue;
    }
    if (char === delimiter && !quoted) {
      result.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  result.push(current);
  return result;
}

function normalizeDelimitedHeaders(values: string[]) {
  const seen = new Map<string, number>();
  return values.map((value, index) => {
    const base = value.trim() || `Column ${index + 1}`;
    const occurrence = (seen.get(base.toLowerCase()) || 0) + 1;
    seen.set(base.toLowerCase(), occurrence);
    return occurrence === 1 ? base : `${base} (${occurrence})`;
  });
}

function isValidImportDate(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
  }
  return !Number.isNaN(Date.parse(value));
}
